import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export const AdminPromoCodes = () => {
  const { t } = useTranslation();
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/admin/promo-codes"); setCodes(r.data || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load promo codes"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/promo-codes", { ...form, discount_value: parseFloat(form.discount_value) });
      setForm({ code: "", discount_type: "percent", discount_value: "" });
      toast.success(t("admin.save"));
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create code"); }
    finally { setSaving(false); }
  };

  const toggle = async (c, active) => {
    try { await api.patch(`/admin/promo-codes/${c.promo_id}`, { active }); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.code}?`)) return;
    try { await api.delete(`/admin/promo-codes/${c.promo_id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="promo-codes-panel">
      <form onSubmit={create} className="bg-white rounded-2xl border border-[#EAE3D9] p-6 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{t("admin.promo_code")}</span>
          <input data-testid="promo-code-input" value={form.code} required
                 onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                 className="input-line uppercase tracking-wider" placeholder="WELCOME10" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{t("admin.promo_type")}</span>
          <select data-testid="promo-type-select" value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  className="input-line bg-transparent">
            <option value="percent">{t("admin.promo_percent")}</option>
            <option value="fixed">{t("admin.promo_fixed")}</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{t("admin.promo_value")}</span>
          <input data-testid="promo-value-input" type="number" min="0.01" step="0.01" required value={form.discount_value}
                 onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="input-line" />
        </label>
        <button type="submit" disabled={saving} data-testid="promo-create-btn"
                className="btn-primary text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
          <Plus className="h-4 w-4" /> {t("admin.promo_new")}
        </button>
        <p className="sm:col-span-4 text-xs text-[#8C7A6B]">{t("admin.promo_hint")}</p>
      </form>

      <div className="bg-white rounded-2xl border border-[#EAE3D9] overflow-hidden">
        <table className="w-full text-sm" data-testid="promo-codes-table">
          <thead className="bg-[#F4EFE6] text-[#8C7A6B] uppercase text-xs tracking-wider">
            <tr>
              <th className="text-start px-5 py-3">{t("admin.promo_code")}</th>
              <th className="text-start px-5 py-3">{t("admin.promo_value")}</th>
              <th className="text-start px-5 py-3">{t("admin.promo_uses")}</th>
              <th className="text-start px-5 py-3">{t("admin.promo_active")}</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.promo_id} className="border-t border-[#EAE3D9]" data-testid={`promo-row-${c.code}`}>
                <td className="px-5 py-4 font-medium tracking-wider text-[#2C2A29]">{c.code}</td>
                <td className="px-5 py-4 text-[#595450]">
                  {c.discount_type === "percent" ? `-${c.discount_value}%` : `-${c.discount_value}`}
                </td>
                <td className="px-5 py-4 text-[#595450]">{c.uses_count || 0}</td>
                <td className="px-5 py-4">
                  <Switch checked={!!c.active} onCheckedChange={(v) => toggle(c, v)} data-testid={`promo-toggle-${c.code}`} />
                </td>
                <td className="px-5 py-4 text-end">
                  <button onClick={() => remove(c)} data-testid={`promo-delete-${c.code}`}
                          className="text-[#8C7A6B] hover:text-[#C86D53] inline-flex items-center gap-1 text-xs">
                    <Trash2 className="h-3 w-3" /> {t("admin.delete")}
                  </button>
                </td>
              </tr>
            ))}
            {codes.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-[#8C7A6B]">{t("admin.promo_none")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
